import React, { ForwardedRef, forwardRef, useEffect } from 'react';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { SelectButton, Item } from '../../Select';
import styles from '@danske/sapphire-css/components/pagination/pagination.module.css';
import { AriaLabelingProps, DOMProps } from '@react-types/shared';
import { PaginationState, usePagination } from './usePagination';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { mergeProps } from 'react-aria/mergeProps';
import { ChevronLeft, ChevronRight } from '@danske/sapphire-icons/react';
import { IconButton } from '../../IconButton';
import intlMessages from '../i18n';
import { useMessageFormatter } from '@react-aria/i18n';
import { PageSelectionRow } from './PageSelectionRow';
import { usePaginationContext } from './usePaginationContext';
import { AriaLabelingPropsKeys, GlobalDomAttributes } from '../../utils/types';
import { clsx } from 'clsx';

export interface PaginationProps
  extends GlobalDomAttributes,
    SapphireStyleProps,
    DOMProps,
    Pick<AriaLabelingProps, AriaLabelingPropsKeys> {
  /** Total data count. */
  itemCount: number;
  /** Options for changing the number of items per page being displayed. */
  pageSizeOptions?: Array<number>;
  /** The current page (controlled). */
  page?: number;
  /** The default page (uncontrolled). */
  defaultPage?: number;
  /** The current pageSize (controlled). */
  pageSize?: number;
  /** The default pageSize (uncontrolled). */
  defaultPageSize?: number;
  /** Callback to handle page and page size change.*/
  onChange?: (newState: PaginationState) => void;
  /** Disable pagination. */
  isDisabled?: boolean;
  /**
   * Allow selection of individual selection
   * @default false
   */
  allowPageSelection?: boolean;
  /**
   * The amount of page numbers to display on either side of the
   * selected page in the page selection row.
   * Note: Only relevant if `allowPageSelection` is `true`.
   *
   * @default 1
   */
  pageSiblingCount?: number;
  /**
   * The amount of page numbers to display on either end of the
   * the page selection row.
   * Note: Only relevant if `allowPageSelection` is `true`.
   *
   * @default 1
   */
  pageBoundaryCount?: number;
}

export const Pagination = forwardRef(function Pagination(
  props: PaginationProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    itemCount,
    page,
    defaultPage,
    pageSize,
    defaultPageSize,
    onChange,
    pageSizeOptions,
    isDisabled,
    allowPageSelection,
    pageSiblingCount = 1,
    pageBoundaryCount = 1,
  } = props;
  const { styleProps } = useSapphireStyleProps(props);
  const formatMessage = useMessageFormatter(intlMessages);
  const domProps = filterDOMProps(props, { global: true, labelable: true });
  const { scrollableRef } = usePaginationContext();

  const {
    currentPageStartIndex,
    currentPageEndIndex,
    currentPageSize,
    changePageSize,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    toPage,
  } = usePagination({
    itemCount,
    page,
    defaultPage,
    pageSize,
    defaultPageSize,
    onChange,
  });

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollableRef?.current?.scrollTo({ top: 0 });
    });
  }, [currentPage, scrollableRef]);

  return (
    <div
      {...mergeProps(styleProps, domProps, {
        className: styles['sapphire-pagination'],
      })}
      ref={ref}
    >
      {pageSizeOptions && (
        <SelectButton
          value={currentPageSize.toString()}
          aria-label={formatMessage('number-of-items-per-page')}
          placeholder={formatMessage('select-an-option')}
          onSelectionChange={(newPageSize) => {
            changePageSize(Number(newPageSize));
          }}
          isDisabled={isDisabled}
          // TODO: remove this hack once the SelectButton does not have hidden
          // padding
          marginLeft="calc(-1 * var(--sapphire-global-size-generic-50))"
          variant="tertiary"
          size="md"
        >
          {Array.from(pageSizeOptions, (e) => {
            return (
              <Item key={e}>
                {formatMessage('items-per-page', { value: e })}
              </Item>
            );
          })}
        </SelectButton>
      )}
      <span className={styles['sapphire-pagination__controls']}>
        {!allowPageSelection && (
          <span
            className={clsx(
              styles['sapphire-pagination__text'],
              styles['sapphire-pagination__text--emphasized']
            )}
          >
            {currentPageStartIndex} - {currentPageEndIndex}
            <span className={styles['sapphire-pagination__text']}>
              {formatMessage('of')}
            </span>
            {itemCount}
          </span>
        )}
        <IconButton
          key="button1"
          variant="tertiary"
          size="md"
          onPress={prevPage}
          isDisabled={currentPageStartIndex === 1 || isDisabled}
          aria-label={formatMessage('previous')}
        >
          <ChevronLeft />
        </IconButton>
        {allowPageSelection && (
          <PageSelectionRow
            currentPage={currentPage}
            totalPageCount={totalPages}
            siblingCount={pageSiblingCount}
            boundaryCount={pageBoundaryCount}
            onPagePress={toPage}
          />
        )}
        <IconButton
          key="button2"
          variant="tertiary"
          size="md"
          onPress={nextPage}
          isDisabled={currentPageEndIndex === itemCount || isDisabled}
          aria-label={formatMessage('next')}
        >
          <ChevronRight />
        </IconButton>
      </span>
    </div>
  );
});
